type UserDataWithMedia = {
  Media?: Array<{ type: string; url: string }> | null
  Caregiver?: { Media?: Array<{ type: string; url: string }> | null } | null
} | null | undefined

/**
 * Get Firebase Auth avatar photoURL
 * Used in console to show Firebase auth avatar separately
 */
export function getFirebaseAuthAvatarSrc(
  firebasePhotoURL: string | null | undefined
): string | null {
  if (!firebasePhotoURL) return null
  const trimmed = firebasePhotoURL.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * Get Media avatar source (User Media first, then Caregiver Media)
 * Used in console to show Media avatar separately
 */
export function getMediaAvatarSrc(
  userData: UserDataWithMedia
): string | null {
  // First priority: User Media images
  const userImage = userData?.Media?.find(m => m.type === 'IMAGE')?.url
  if (userImage) return userImage

  // Second priority: Caregiver Media images
  const caregiverImage = userData?.Caregiver?.Media?.find(m => m.type === 'IMAGE')?.url
  if (caregiverImage) return caregiverImage

  return null
}

/**
 * Get the best avatar source for public-facing app (menu, etc.)
 * Priority: Media images > Firebase auth photoURL (current user only) > null
 * 
 * Note: Firebase auth photoURL is only available for the current user via client-side auth.
 * For other users, only Media images will be used (no Firebase auth fallback).
 * 
 * @param userData - User data with Media (can be current user or other user)
 * @param currentUserPhotoURL - Firebase auth photoURL (only pass for current user, null for others)
 */
export function getPublicAvatarSrc(
  userData: UserDataWithMedia,
  currentUserPhotoURL: string | null | undefined
): string | null {
  // First priority: Media images (User Media first, then Caregiver Media)
  const mediaAvatar = getMediaAvatarSrc(userData)
  if (mediaAvatar) return mediaAvatar

  // Second priority: Firebase auth photoURL (only available for current user)
  // Only use if provided (meaning it's the current user viewing their own profile)
  if (currentUserPhotoURL) {
    const firebaseAvatar = getFirebaseAuthAvatarSrc(currentUserPhotoURL)
    if (firebaseAvatar) return firebaseAvatar
  }

  // Fallback: null (will show default avatar)
  return null
}

/**
 * @deprecated Use getPublicAvatarSrc instead
 * Kept for backward compatibility
 */
export function getAvatarSrc(
  userData: UserDataWithMedia,
  firebasePhotoURL: string | null | undefined
): string | null {
  return getPublicAvatarSrc(userData, firebasePhotoURL)
}
